import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EmptyState from '../../../common-components/EmptyState';
import ConfirmDialog from '../../../common-components/ConfirmDialog';
import { useAppDispatch, useAppSelector } from '../../../helpers/hooks';
import { useWorkspaceSlice } from '../slice';
import {
  selectBusyKeyId,
  selectJobLocked,
  selectKeys,
  selectKeysLoading,
  selectKeysMeta,
  selectKeysSearch,
  selectProject,
  selectSelectedLanguage,
  selectTranslations,
} from '../slice/selectors';
import { TranslationKey } from '../../../helpers/types';
import KeyRow from './KeyRow';
import CreateKeyDialog from './CreateKeyDialog';
import ImportKeysDialog from './ImportKeysDialog';

interface TranslationsTabProps {
  projectId: string;
}

export default function TranslationsTab({ projectId }: TranslationsTabProps) {
  const dispatch = useAppDispatch();
  const { actions } = useWorkspaceSlice();

  const project = useAppSelector(selectProject);
  const keys = useAppSelector(selectKeys);
  const meta = useAppSelector(selectKeysMeta);
  const keysLoading = useAppSelector(selectKeysLoading);
  const search = useAppSelector(selectKeysSearch);
  const translations = useAppSelector(selectTranslations);
  const busyKeyId = useAppSelector(selectBusyKeyId);
  const locked = useAppSelector(selectJobLocked);
  const selectedLanguage = useAppSelector(selectSelectedLanguage);

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toDelete, setToDelete] = useState<TranslationKey | null>(null);
  const [searchInput, setSearchInput] = useState(search);

  const runSearch = (value: string) => {
    dispatch(actions.setKeysSearch(value));
    dispatch(actions.fetchKeysRequest({ projectId, page: 1, search: value }));
  };

  const handlePageChange = (_e: unknown, page: number) => {
    dispatch(
      actions.fetchKeysRequest({ projectId, page: page + 1, search }),
    );
  };

  const confirmDelete = () => {
    if (toDelete) {
      dispatch(actions.deleteKeyRequest({ projectId, keyId: toDelete.id }));
      setToDelete(null);
    }
  };

  const handleTranslateMissing = () => {
    if (selectedLanguage) {
      dispatch(
        actions.startTranslateJobRequest({
          projectId,
          languages: [selectedLanguage],
        }),
      );
    }
  };

  const noTargetLanguages = !project?.targetLanguages.length;

  if (noTargetLanguages) {
    return (
      <EmptyState
        title="No target languages"
        description="Add a target language under Settings before adding translations."
      />
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search keys…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch(searchInput);
          }}
          sx={{ maxWidth: 360, width: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={locked}
            onClick={() => setImportOpen(true)}
          >
            Import JSON
          </Button>
          <Button
            variant="outlined"
            startIcon={
              locked ? <CircularProgress size={16} /> : <AutoAwesomeIcon />
            }
            disabled={locked}
            onClick={handleTranslateMissing}
          >
            Translate untranslated
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={locked}
            onClick={() => setCreateOpen(true)}
          >
            Add key
          </Button>
        </Stack>
      </Stack>

      {keysLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : keys.length === 0 ? (
        <EmptyState
          title="No keys yet"
          description="Add your first translation key to get started."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={locked}
              onClick={() => setCreateOpen(true)}
            >
              Add key
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Translation ({selectedLanguage})</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((keyItem) => (
                <KeyRow
                  key={keyItem.id}
                  projectId={projectId}
                  language={selectedLanguage as string}
                  keyItem={keyItem}
                  translation={translations[keyItem.id]}
                  busy={busyKeyId === keyItem.id}
                  locked={locked}
                  onDelete={setToDelete}
                />
              ))}
            </TableBody>
          </Table>
          {meta && (
            <TablePagination
              component="div"
              count={meta.total}
              page={meta.page - 1}
              rowsPerPage={meta.limit}
              rowsPerPageOptions={[meta.limit]}
              onPageChange={handlePageChange}
            />
          )}
        </TableContainer>
      )}

      <CreateKeyDialog
        open={createOpen}
        projectId={projectId}
        onClose={() => setCreateOpen(false)}
      />
      <ImportKeysDialog
        open={importOpen}
        projectId={projectId}
        onClose={() => setImportOpen(false)}
      />
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete key"
        message={`Delete "${toDelete?.key}" and all its translations?`}
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
}
